import { useState, useEffect, useCallback } from 'react'
import { getNexusClient } from '@/lib/supabase/client'
import type { AdminUserRow } from '@/lib/supabase/types'

export interface AdminUser {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  role: 'super_admin' | 'content_manager' | 'support'
  avatarUrl: string | null
  isActive: boolean
  lastLoginAt: string | null
  createdAt: string
  updatedAt: string
}

function mapRowToUser(row: AdminUserRow): AdminUser {
  return {
    id: row.id,
    email: row.email,
    firstName: row.first_name,
    lastName: row.last_name,
    role: row.role,
    avatarUrl: row.avatar_url,
    isActive: row.is_active,
    lastLoginAt: row.last_login_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

interface UseAdminUsersOptions {
  search?: string
  role?: 'super_admin' | 'content_manager' | 'support' | 'all'
  activeOnly?: boolean
}

export function useAdminUsers(options: UseAdminUsersOptions = {}) {
  const { search = '', role = 'all', activeOnly = false } = options

  const [users, setUsers] = useState<AdminUser[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchUsers = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()
      let query = client.from('admin_users').select('*')

      // Apply search filter
      if (search) {
        query = query.or(`email.ilike.%${search}%,first_name.ilike.%${search}%,last_name.ilike.%${search}%`)
      }

      // Apply role filter
      if (role !== 'all') {
        query = query.eq('role', role)
      }

      // Apply active filter
      if (activeOnly) {
        query = query.eq('is_active', true)
      }

      query = query.order('created_at', { ascending: false })

      const { data, error: queryError } = await query

      if (queryError) throw queryError

      setUsers((data || []).map(mapRowToUser))
    } catch (err: any) {
      console.error('Error fetching admin users:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [search, role, activeOnly])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  return { users, isLoading, error, refetch: fetchUsers }
}

export interface CreateAdminUserInput {
  email: string
  firstName?: string
  lastName?: string
  role: 'super_admin' | 'content_manager' | 'support'
  password: string
}

export interface UpdateAdminUserInput {
  firstName?: string
  lastName?: string
  role?: 'super_admin' | 'content_manager' | 'support'
  isActive?: boolean
  avatarUrl?: string
}

export function useAdminUserMutations() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const createUser = async (input: CreateAdminUserInput): Promise<AdminUser | null> => {
    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      // First, create the user in Supabase Auth
      const { data: authData, error: authError } = await client.auth.admin.createUser({
        email: input.email,
        password: input.password,
        email_confirm: true,
      })

      if (authError) {
        // If admin API fails, try regular signUp (for non-admin contexts)
        const { data: signUpData, error: signUpError } = await client.auth.signUp({
          email: input.email,
          password: input.password,
        })

        if (signUpError) throw signUpError

        if (!signUpData.user) throw new Error('Failed to create auth user')

        // Insert into admin_users table
        const { data, error: insertError } = await client
          .from('admin_users')
          .insert({
            id: signUpData.user.id,
            email: input.email,
            first_name: input.firstName || null,
            last_name: input.lastName || null,
            role: input.role,
            is_active: true,
            avatar_url: null,
            last_login_at: null,
          } as any)
          .select()
          .single()
          .returns<AdminUserRow>()

        if (insertError) throw insertError
        return mapRowToUser(data as AdminUserRow)
      }

      if (!authData.user) throw new Error('Failed to create auth user')

      // Insert into admin_users table
      const { data, error: insertError } = await client
        .from('admin_users')
        .insert({
          id: authData.user.id,
          email: input.email,
          first_name: input.firstName || null,
          last_name: input.lastName || null,
          role: input.role,
          is_active: true,
          avatar_url: null,
          last_login_at: null,
        } as any)
        .select()
        .single()
        .returns<AdminUserRow>()

      if (insertError) throw insertError
      return mapRowToUser(data as AdminUserRow)
    } catch (err: any) {
      console.error('Error creating admin user:', err)
      setError(err.message)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const updateUser = async (userId: string, input: UpdateAdminUserInput): Promise<AdminUser | null> => {
    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      const updateData: Record<string, any> = {}
      if (input.firstName !== undefined) updateData.first_name = input.firstName
      if (input.lastName !== undefined) updateData.last_name = input.lastName
      if (input.role !== undefined) updateData.role = input.role
      if (input.isActive !== undefined) updateData.is_active = input.isActive
      if (input.avatarUrl !== undefined) updateData.avatar_url = input.avatarUrl

      const { data, error: updateError } = await (client as any)
        .from('admin_users')
        .update(updateData)
        .eq('id', userId)
        .select()
        .single()

      if (updateError) throw updateError
      return mapRowToUser(data as AdminUserRow)
    } catch (err: any) {
      console.error('Error updating admin user:', err)
      setError(err.message)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const deleteUser = async (userId: string): Promise<boolean> => {
    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      // Delete from admin_users table (Supabase Auth user remains but can't access admin)
      const { error: deleteError } = await client
        .from('admin_users')
        .delete()
        .eq('id', userId)

      if (deleteError) throw deleteError
      return true
    } catch (err: any) {
      console.error('Error deleting admin user:', err)
      setError(err.message)
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const toggleUserActive = async (userId: string, isActive: boolean): Promise<boolean> => {
    const result = await updateUser(userId, { isActive })
    return result !== null
  }

  return {
    createUser,
    updateUser,
    deleteUser,
    toggleUserActive,
    isLoading,
    error,
  }
}
